import type { ReactElement } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";

import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { Navigate, NoContextOutlet } from "src/router";
import { PermissionedRoute } from "src/components/router/permissioned-route";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { useUser } from "src/contexts/user-context";
import { isHightouchStaffUser } from "src/utils/user";
import { lazyNamed } from "src/utils/lazy-named";
import { useSuggestedReportsEnabled } from "./hooks/use-suggested-reports-enabled";

// Editing the insight prompt is a Hightouch-employee action (mirrors the
// requireImpersonator gate on the resolver), so the route is staff-only rather
// than gated on a workspace permission.
const InitiativeStaffRoute = ({ children }: { children: ReactElement }) => {
  const { user } = useUser();
  const { pretendImNotAHightouchAdmin } = useFlags();
  if (
    !isHightouchStaffUser(user?.admin_type, { pretendImNotAHightouchAdmin })
  ) {
    return <Navigate to="/agents/initiatives" replace />;
  }
  return children;
};

// Staff-only playground for rendering any workspace's report card by run ID
// (mirrors the staff gate on the reportCardByRunId resolver).
const ReportCardPlaygroundStaffRoute = ({
  children,
}: {
  children: ReactElement;
}) => {
  const { user } = useUser();
  const { pretendImNotAHightouchAdmin } = useFlags();
  if (
    !isHightouchStaffUser(user?.admin_type, { pretendImNotAHightouchAdmin })
  ) {
    return <Navigate to="/agents/reports" replace />;
  }
  return children;
};

const agentsPageImport = () => import(".");
const agentsLayoutImport = () => import("./layout");
const AgentsPage = lazyNamed(agentsPageImport, "AgentsPage");
const AgentsSession = lazyNamed(() => import("./session"), "AgentsSession");
const AgentsLayout = lazyNamed(agentsLayoutImport, "AgentsLayout");
const AgentsChatsLayout = lazyNamed(agentsLayoutImport, "AgentsChatsLayout");

export const preloads: RoutePreload[] = [
  ["/agents", () => Promise.all([agentsLayoutImport(), agentsPageImport()])],
];
const AgentsSettings = lazyNamed(() => import("./settings"), "AgentsSettings");
const CreateReportPage = lazyNamed(
  () => import("./reports/create-report"),
  "CreateReportPage",
);
const CreateReportWizard = lazyNamed(
  () => import("./reports/create-report-wizard"),
  "CreateReportWizard",
);
const ReportPage = lazyNamed(() => import("./reports/report"), "ReportPage");
const ReportConfigurationTab = lazyNamed(
  () => import("./reports/report-configuration"),
  "ReportConfigurationTab",
);
const ReportsPage = lazyNamed(() => import("./reports"), "ReportsPage");
const ReportLatestRunRedirect = lazyNamed(
  () => import("./reports/report-latest-run-redirect"),
  "ReportLatestRunRedirect",
);
const ReportRunPage = lazyNamed(
  () => import("./reports/report-run-page"),
  "ReportRunPage",
);
const ReportRunView = lazyNamed(
  () => import("./reports/report-run-view"),
  "ReportRunView",
);
const ReportReviewPage = lazyNamed(
  () => import("./reports/report-review-page"),
  "ReportReviewPage",
);
const ReportCardPlayground = lazyNamed(
  () => import("./reports/card-playground"),
  "ReportCardPlayground",
);
const ReportCardPlaygroundRun = lazyNamed(
  () => import("./reports/card-playground"),
  "ReportCardPlaygroundRun",
);
const LegacyAgentsRedirect = lazyNamed(
  () => import("./components/agents-redirects"),
  "LegacyAgentsRedirect",
);
const LegacyReportsRedirect = lazyNamed(
  () => import("./components/agents-redirects"),
  "LegacyReportsRedirect",
);
const LegacyCustomAgentsRedirect = lazyNamed(
  () => import("./components/agents-redirects"),
  "LegacyCustomAgentsRedirect",
);
const CustomAgentsPage = lazyNamed(
  () => import("./custom-agents"),
  "CustomAgentsPage",
);
const NewCustomAgentPage = lazyNamed(
  () => import("./custom-agents/create"),
  "NewCustomAgentPage",
);
const EditCustomAgentPage = lazyNamed(
  () => import("./custom-agents/edit"),
  "EditCustomAgentPage",
);
const InitiativesPage = lazyNamed(
  () => import("./initiatives"),
  "InitiativesPage",
);
const InitiativeDetailPage = lazyNamed(
  () => import("./initiatives/detail"),
  "InitiativeDetailPage",
);
const InitiativePromptPage = lazyNamed(
  () => import("./initiatives/prompt"),
  "InitiativePromptPage",
);
const GeneralSettings = lazyNamed(
  () => import("./settings/general"),
  "GeneralSettings",
);
const AgentsUsageTab = lazyNamed(() => import("./usage"), "AgentsUsageTab");

const CreateReportWizardRoute = () => {
  const suggestedReportsEnabled = useSuggestedReportsEnabled();
  if (!suggestedReportsEnabled) {
    return <Navigate to="/agents/reports" replace />;
  }
  return <CreateReportWizard />;
};

export const routes: RouteObject[] = [
  // keep copilot route for redirect to agents
  {
    path: "agents/:sessionId",
    spacesAccess: "hidden-from-limited-view",
    element: <LegacyAgentsRedirect />,
  },
  // Legacy /reports/* redirects to /agents/reports/*
  {
    path: "reports/*",
    spacesAccess: "hidden-from-limited-view",
    element: <LegacyReportsRedirect />,
  },
  // Legacy /custom-agents/* redirects to /agents/custom-agents/*
  {
    path: "custom-agents/*",
    spacesAccess: "hidden-from-limited-view",
    element: <LegacyCustomAgentsRedirect />,
  },
  // Main agents routes
  {
    path: "agents",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <PermissionedRoute
        permission={{
          v2: { resource: "workspace", grant: "can_view_agents" },
        }}
        redirect="/"
      >
        <AgentsLayout />
      </PermissionedRoute>
    ),
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="chats" replace />,
      },
      // Chats section (with chat history sidebar)
      {
        path: "chats",
        spacesAccess: "hidden-from-limited-view",
        element: <AgentsChatsLayout />,
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <AgentsPage />,
          },
          {
            path: ":sessionId",
            spacesAccess: "hidden-from-limited-view",
            element: <AgentsSession />,
          },
        ],
      },
      // Reports section
      {
        path: "reports",
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <ReportsPage />,
          },
          {
            path: "new",
            spacesAccess: "hidden-from-limited-view",
            element: <CreateReportPage />,
          },
          {
            path: "create",
            spacesAccess: "hidden-from-limited-view",
            element: <CreateReportWizardRoute />,
          },
          // Landing route for a report: forwards to the latest generated
          // report (or configuration when none exist yet).
          {
            path: ":id/runs",
            spacesAccess: "hidden-from-limited-view",
            element: <ReportLatestRunRedirect />,
          },
          {
            path: ":id/runs/:runId",
            spacesAccess: "hidden-from-limited-view",
            element: <ReportRunPage />,
            children: [
              {
                index: true,
                spacesAccess: "hidden-from-limited-view",
                element: <ReportRunView />,
              },
              {
                path: "review",
                spacesAccess: "hidden-from-limited-view",
                element: <ReportReviewPage />,
              },
            ],
          },
          {
            path: "card-playground",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <ReportCardPlaygroundStaffRoute>
                <ReportCardPlayground />
              </ReportCardPlaygroundStaffRoute>
            ),
          },
          {
            path: "card-playground/:runId",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <ReportCardPlaygroundStaffRoute>
                <ReportCardPlaygroundRun />
              </ReportCardPlaygroundStaffRoute>
            ),
          },
          {
            path: ":id",
            spacesAccess: "hidden-from-limited-view",
            element: <ReportPage />,
            children: [
              {
                index: true,
                spacesAccess: "hidden-from-limited-view",
                element: <Navigate to="runs" replace />,
              },
              {
                path: "configuration",
                spacesAccess: "hidden-from-limited-view",
                element: <ReportConfigurationTab />,
              },
            ],
          },
        ],
      },
      // Custom agents section
      {
        path: "custom-agents",
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <CustomAgentsPage />,
          },
          {
            path: "new",
            spacesAccess: "hidden-from-limited-view",
            element: <NewCustomAgentPage />,
          },
          {
            path: ":id",
            spacesAccess: "hidden-from-limited-view",
            element: <EditCustomAgentPage />,
          },
        ],
      },
      // Initiatives section (gated behind the agenticInitiatives flag)
      {
        path: "initiatives",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="agenticInitiatives"
            redirect="/agents/chats"
          >
            <NoContextOutlet />
          </FeatureFlagRoute>
        ),
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <InitiativesPage />,
          },
          {
            path: "prompt",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <InitiativeStaffRoute>
                <InitiativePromptPage />
              </InitiativeStaffRoute>
            ),
          },
          {
            path: ":id",
            spacesAccess: "hidden-from-limited-view",
            element: <InitiativeDetailPage />,
          },
        ],
      },
      // Settings section
      {
        path: "settings",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <PermissionedRoute
            permission={{
              v2: { resource: "workspace", grant: "can_configure_agents" },
            }}
            redirect="/agents/chats"
          >
            <AgentsSettings />
          </PermissionedRoute>
        ),
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="general" replace />,
          },
          {
            path: "general",
            spacesAccess: "hidden-from-limited-view",
            element: <GeneralSettings />,
          },
          {
            path: "usage",
            spacesAccess: "hidden-from-limited-view",
            element: <AgentsUsageTab />,
          },
        ],
      },
    ],
  },
];
